import React, { useRef, useEffect, useState } from "react";
import "../css/PaintEditor.css";
import { DEFAULT_STAGE_GRID_WIDTH, DEFAULT_STAGE_GRID_HEIGHT } from "../constants/stageDimensions";

const COLORS = [
  "#ffdff4", "#ffb4d9", "#ffaad7", "#ff89bc", "#ff3266", "#ff322e", "#ff7f29",
  "#ffd700", "#feffbe", "#deff87", "#a5fc6d", "#5eda9e", "#68acf4", "#068afb",
  "#a19cff", "#edb8ff", "#ffb8fd", "#d8cbc4", "#fffff6", "#e4e0da",
  "#bbbbbb", "#918991", "#74564d", "#42362b", "#191818", "#000000"
];

const THICKNESS_LEVELS = [2, 4, 7, 11];

const BASE_CANVAS_WIDTH = 1000;
const CANVAS_WIDTH = BASE_CANVAS_WIDTH;
const CANVAS_HEIGHT = Math.round(
  BASE_CANVAS_WIDTH * (DEFAULT_STAGE_GRID_HEIGHT / DEFAULT_STAGE_GRID_WIDTH)
);

// simple deep clone for undo/redo snapshots
const cloneShapes = (arr) => JSON.parse(JSON.stringify(arr));

export default function PaintEditor({ onSave, onClose, initialHeading = "", onHeadingChange }) {
  const canvasRef = useRef(null);

  // vector layer state
  const [shapes, setShapes] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // tools & style
  const [tool, setTool] = useState("pen"); // pen | rect | ellipse | line | eraser | move | fill
  const [color, setColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(4);

  // interaction state
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftShape, setDraftShape] = useState(null); // for rect/ellipse/line
  const [activeShapeId, setActiveShapeId] = useState(null);
  const dragInfoRef = useRef(null); // { shapeId, originalShape, start:{x,y} }

  const [headingText, setHeadingText] = useState(initialHeading || "");

  // redraw whenever shapes or draft change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    drawAll(ctx);
  }, [shapes, draftShape, activeShapeId]);

  useEffect(() => {
    setHeadingText(initialHeading || "");
  }, [initialHeading]);

  const handleHeadingChange = (e) => {
    const value = e.target.value;
    setHeadingText(value);
    if (typeof onHeadingChange === "function") {
      onHeadingChange(value);
    }
  };

  // ----------------------------------------------------
  // drawing helpers
  // ----------------------------------------------------
  function drawAll(ctx) {
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    drawGrid(ctx);

    shapes.forEach((shape) => drawShape(ctx, shape));

    if (draftShape) drawShape(ctx, draftShape);

    if (activeShapeId) {
      const sel = shapes.find((s) => s.id === activeShapeId);
      if (sel) drawSelection(ctx, sel);
    }
  }

  function drawGrid(ctx) {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.strokeStyle = "#efebe2";

    for (let x = 0.5; x < CANVAS_WIDTH; x += 20) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, CANVAS_HEIGHT);
      ctx.stroke();
    }

    for (let y = 0.5; y < CANVAS_HEIGHT; y += 20) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(CANVAS_WIDTH, y);
      ctx.stroke();
    }
  }

  function getShapeCenter(s) {
    switch (s.type) {
      case "rect":
        return { cx: s.x + s.w / 2, cy: s.y + s.h / 2 };
      case "ellipse":
        return { cx: s.cx, cy: s.cy };
      case "line":
        return { cx: (s.x1 + s.x2) / 2, cy: (s.y1 + s.y2) / 2 };
      case "pen": {
        const pts = s.points || [];
        if (!pts.length) return { cx: 0, cy: 0 };
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const w = Math.max(...xs) - x;
        const h = Math.max(...ys) - y;
        return { cx: x + w / 2, cy: y + h / 2 };
      }
      case "triangle": {
        const pts = s.points || [];
        if (pts.length < 3) return { cx: 0, cy: 0 };
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        return { cx: minX + (maxX - minX) / 2, cy: minY + (maxY - minY) / 2 };
      }
      default:
        return { cx: 0, cy: 0 };
    }
  }

  function drawShape(ctx, s) {
    const angleRad = ((s.rotation || 0) * Math.PI) / 180;
    const { cx, cy } = getShapeCenter(s);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angleRad);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = s.strokeWidth || 3;
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.fillColor || "transparent";

    switch (s.type) {
      case "pen": {
        const pts = s.points || [];
        if (pts.length < 2) break;
        ctx.beginPath();
        ctx.moveTo(pts[0].x - cx, pts[0].y - cy);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x - cx, pts[i].y - cy);
        }
        ctx.stroke();
        break;
      }
      case "rect": {
        const x = s.x - cx;
        const y = s.y - cy;
        if (s.fillColor) {
          ctx.beginPath();
          ctx.rect(x, y, s.w, s.h);
          ctx.fill();
        }
        ctx.strokeRect(x, y, s.w, s.h);
        break;
      }
      case "ellipse": {
        ctx.beginPath();
        ctx.ellipse(
          0,
          0,
          Math.abs(s.rx),
          Math.abs(s.ry),
          0,
          0,
          Math.PI * 2
        );
        if (s.fillColor) ctx.fill();
        ctx.stroke();
        break;
      }
      case "line": {
        ctx.beginPath();
        ctx.moveTo(s.x1 - cx, s.y1 - cy);
        ctx.lineTo(s.x2 - cx, s.y2 - cy);
        ctx.stroke();
        break;
      }
      case "triangle": {
        const pts = s.points || [];
        if (pts.length < 3) break;
        ctx.beginPath();
        ctx.moveTo(pts[0].x - cx, pts[0].y - cy);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x - cx, pts[i].y - cy);
        }
        ctx.closePath();
        if (s.fillColor) ctx.fill();
        ctx.stroke();
        break;
      }
      default:
        break;
    }

    ctx.restore();
  }

  function drawSelection(ctx, s) {
    const angleRad = ((s.rotation || 0) * Math.PI) / 180;
    const { cx, cy } = getShapeCenter(s);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angleRad);
    ctx.strokeStyle = "#29A6FF";
    ctx.lineWidth = 2;

    switch (s.type) {
      case "pen": {
        const pts = s.points || [];
        if (pts.length < 2) break;
        ctx.beginPath();
        ctx.moveTo(pts[0].x - cx, pts[0].y - cy);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x - cx, pts[i].y - cy);
        }
        ctx.stroke();
        break;
      }
      case "rect": {
        const x = s.x - cx;
        const y = s.y - cy;
        ctx.strokeRect(x, y, s.w, s.h);
        break;
      }
      case "ellipse": {
        ctx.beginPath();
        ctx.ellipse(
          0,
          0,
          Math.abs(s.rx),
          Math.abs(s.ry),
          0,
          0,
          Math.PI * 2
        );
        ctx.stroke();
        break;
      }
      case "line": {
        ctx.beginPath();
        ctx.moveTo(s.x1 - cx, s.y1 - cy);
        ctx.lineTo(s.x2 - cx, s.y2 - cy);
        ctx.stroke();
        break;
      }
      case "triangle": {
        const pts = s.points || [];
        if (pts.length < 3) break;
        ctx.beginPath();
        ctx.moveTo(pts[0].x - cx, pts[0].y - cy);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x - cx, pts[i].y - cy);
        }
        ctx.closePath();
        ctx.stroke();
        break;
      }
      default:
        break;
    }

    ctx.restore();
  }

  // ----------------------------------------------------
  // pointer helpers
  // ----------------------------------------------------
  function getCanvasCoords(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  // ----------------------------------------------------
  // undo / redo
  // ----------------------------------------------------
  function pushUndoSnapshot() {
    setUndoStack((prev) => [...prev, cloneShapes(shapes)]);
    setRedoStack([]);
  }

  const handleUndo = () => {
    if (!undoStack.length) return;
    const last = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, -1));
    setRedoStack((prev) => [...prev, cloneShapes(shapes)]);
    setShapes(cloneShapes(last));
    setActiveShapeId(null);
    setDraftShape(null);
  };

  const handleRedo = () => {
    if (!redoStack.length) return;
    const last = redoStack[redoStack.length - 1];
    setRedoStack((prev) => prev.slice(0, -1));
    setUndoStack((prev) => [...prev, cloneShapes(shapes)]);
    setShapes(cloneShapes(last));
    setActiveShapeId(null);
    setDraftShape(null);
  };

  // ----------------------------------------------------
  // hit testing
  // (rotation is approximated – uses axis-aligned shape for simplicity)
  // ----------------------------------------------------
  function pointInTriangle(px, py, pts = []) {
    if (pts.length < 3) return false;
    const [a, b, c] = pts;
    const area = (v1, v2, v3) =>
      Math.abs(
        (v1.x * (v2.y - v3.y) +
          v2.x * (v3.y - v1.y) +
          v3.x * (v1.y - v2.y)) / 2
      );
    const areaABC = area(a, b, c);
    const areaPBC = area({ x: px, y: py }, b, c);
    const areaPAC = area(a, { x: px, y: py }, c);
    const areaPAB = area(a, b, { x: px, y: py });
    return Math.abs(areaABC - (areaPBC + areaPAC + areaPAB)) < 0.5;
  }

  function hitTestShape(s, x, y) {
    switch (s.type) {
      case "rect": {
        const x1 = s.x;
        const y1 = s.y;
        const x2 = s.x + s.w;
        const y2 = s.y + s.h;
        return (
          x >= Math.min(x1, x2) &&
          x <= Math.max(x1, x2) &&
          y >= Math.min(y1, y2) &&
          y <= Math.max(y1, y2)
        );
      }
      case "ellipse": {
        const dx = (x - s.cx) / (s.rx || 1);
        const dy = (y - s.cy) / (s.ry || 1);
        return dx * dx + dy * dy <= 1;
      }
      case "line": {
        const { x1, y1, x2, y2 } = s;
        const dist =
          Math.abs((y2 - y1) * x - (x2 - x1) * y + x2 * y1 - y2 * x1) /
          Math.hypot(y2 - y1, x2 - x1 || 1);
        return dist < 6;
      }
      case "pen": {
        const pts = s.points || [];
        return pts.some((p) => Math.hypot(p.x - x, p.y - y) < 6);
      }
      case "triangle":
        return pointInTriangle(x, y, s.points);
      default:
        return false;
    }
  }

  function findTopShapeAt(x, y) {
    for (let i = shapes.length - 1; i >= 0; i--) {
      if (hitTestShape(shapes[i], x, y)) return shapes[i];
    }
    return null;
  }

  // ----------------------------------------------------
  // pointer handlers
  // ----------------------------------------------------
  const handlePointerDown = (e) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const { x, y } = getCanvasCoords(e);

    if (tool === "pen") {
      pushUndoSnapshot();
      const id = Date.now();
      const newShape = {
        id,
        type: "pen",
        color,
        strokeWidth,
        points: [{ x, y }],
        rotation: 0
      };
      setShapes((prev) => [...prev, newShape]);
      setActiveShapeId(id);
      setIsDrawing(true);
      return;
    }

    if (tool === "eraser") {
      pushUndoSnapshot();
      eraseAt(x, y);
      setIsDrawing(true);
      return;
    }

    if (
      tool === "rect" ||
      tool === "ellipse" ||
      tool === "line" ||
      tool === "triangle"
    ) {
      pushUndoSnapshot();
      const id = Date.now();
      if (tool === "rect") {
        setDraftShape({
          id,
          type: "rect",
          color,
          strokeWidth,
          x,
          y,
          w: 0,
          h: 0,
          rotation: 0
        });
      } else if (tool === "ellipse") {
        setDraftShape({
          id,
          type: "ellipse",
          color,
          strokeWidth,
          cx: x,
          cy: y,
          rx: 0,
          ry: 0,
          rotation: 0
        });
      } else if (tool === "line") {
        setDraftShape({
          id,
          type: "line",
          color,
          strokeWidth,
          x1: x,
          y1: y,
          x2: x,
          y2: y,
          rotation: 0
        });
      } else if (tool === "triangle") {
        setDraftShape({
          id,
          type: "triangle",
          color,
          strokeWidth,
          points: [
            { x, y },
            { x, y },
            { x, y }
          ],
          startX: x,
          startY: y,
          rotation: 0
        });
      }
      setIsDrawing(true);
      setActiveShapeId(null);
      return;
    }

    if (tool === "move") {
      const hit = findTopShapeAt(x, y);
      if (hit) {
        pushUndoSnapshot();
        setActiveShapeId(hit.id);
        dragInfoRef.current = {
          shapeId: hit.id,
          originalShape: cloneShapes([hit])[0],
          start: { x, y }
        };
      } else {
        setActiveShapeId(null);
        dragInfoRef.current = null;
      }
      return;
    }

    if (tool === "fill") {
      const hit = findTopShapeAt(x, y);
      if (hit) {
        pushUndoSnapshot();
        setShapes((prev) =>
          prev.map((s) =>
            s.id === hit.id ? { ...s, fillColor: color } : s
          )
        );
        setActiveShapeId(hit.id);
      }
      return;
    }
  };

  const handlePointerMove = (e) => {
    if (!canvasRef.current) return;
    if (!isDrawing && !dragInfoRef.current) return;

    e.preventDefault();
    const { x, y } = getCanvasCoords(e);

    if (tool === "pen" && isDrawing) {
      setShapes((prev) =>
        prev.map((s) =>
          s.id === activeShapeId
            ? { ...s, points: [...(s.points || []), { x, y }] }
            : s
        )
      );
      return;
    }

    if (tool === "eraser" && isDrawing) {
      eraseAt(x, y);
      return;
    }

    if (isDrawing && draftShape) {
      if (draftShape.type === "rect") {
        const w = x - draftShape.x;
        const h = y - draftShape.y;
        setDraftShape((prev) => ({ ...prev, w, h }));
      } else if (draftShape.type === "ellipse") {
        setDraftShape((prev) => ({
          ...prev,
          rx: x - prev.cx,
          ry: y - prev.cy
        }));
      } else if (draftShape.type === "line") {
        setDraftShape((prev) => ({
          ...prev,
          x2: x,
          y2: y
        }));
      } else if (draftShape.type === "triangle") {
        setDraftShape((prev) => {
          if (!prev) return prev;
          const startX = prev.startX ?? x;
          const startY = prev.startY ?? y;
          const minX = Math.min(startX, x);
          const maxX = Math.max(startX, x);
          const minY = Math.min(startY, y);
          const maxY = Math.max(startY, y);
          const midX = (minX + maxX) / 2;
          return {
            ...prev,
            points: [
              { x: midX, y: minY },
              { x: maxX, y: maxY },
              { x: minX, y: maxY }
            ]
          };
        });
      }
      return;
    }

    if (tool === "move" && dragInfoRef.current) {
      const { shapeId, originalShape, start } = dragInfoRef.current;
      const dx = x - start.x;
      const dy = y - start.y;
      setShapes((prev) =>
        prev.map((s) =>
          s.id === shapeId ? translateShape(originalShape, dx, dy) : s
        )
      );
      return;
    }
  };

  const handlePointerUp = () => {
    if (isDrawing && draftShape) {
      const finalized =
        draftShape.type === "triangle"
          ? (({ startX, startY, ...rest }) => rest)(draftShape)
          : draftShape;
      setShapes((prev) => [...prev, finalized]);
      setActiveShapeId(finalized.id);
      setDraftShape(null);
    }
    setIsDrawing(false);
    dragInfoRef.current = null;
  };

  function translateShape(shape, dx, dy) {
    const s = cloneShapes([shape])[0];
    switch (s.type) {
      case "pen":
        s.points = (s.points || []).map((p) => ({
          x: p.x + dx,
          y: p.y + dy
        }));
        break;
      case "rect":
        s.x += dx;
        s.y += dy;
        break;
      case "ellipse":
        s.cx += dx;
        s.cy += dy;
        break;
      case "line":
        s.x1 += dx;
        s.y1 += dy;
        s.x2 += dx;
        s.y2 += dy;
        break;
      case "triangle":
        s.points = (s.points || []).map((p) => ({
          x: p.x + dx,
          y: p.y + dy
        }));
        break;
      default:
        break;
    }
    return s;
  }

  function eraseAt(x, y) {
    setShapes((prev) => prev.filter((s) => !hitTestShape(s, x, y)));
    if (activeShapeId && !shapes.find((s) => s.id === activeShapeId)) {
      setActiveShapeId(null);
    }
  }

  // ----------------------------------------------------
  // right-actions: move, rotate, stamp, cut, fill, ok
  // ----------------------------------------------------
  const handleMoveTool = () => setTool("move");

  const handleRotate = () => {
    if (!activeShapeId) return;
    pushUndoSnapshot();
    setShapes((prev) =>
      prev.map((s) =>
        s.id === activeShapeId
          ? { ...s, rotation: (s.rotation || 0) + 15 }
          : s
      )
    );
  };

  const handleStamp = () => {
    if (!activeShapeId) return;
    const src = shapes.find((s) => s.id === activeShapeId);
    if (!src) return;
    pushUndoSnapshot();
    const copy = cloneShapes([src])[0];
    copy.id = Date.now();
    const shifted = translateShape(copy, 10, 10);
    setShapes((prev) => [...prev, shifted]);
    setActiveShapeId(shifted.id);
  };

  const handleCut = () => {
    if (!activeShapeId) return;
    pushUndoSnapshot();
    setShapes((prev) => prev.filter((s) => s.id !== activeShapeId));
    setActiveShapeId(null);
  };

  const handleFillTool = () => setTool("fill");

  // ----------------------------------------------------
  // SVG builder (stored globally, PNG returned via onSave)
  // ----------------------------------------------------
  function buildSvg(shapesList) {
    const width = CANVAS_WIDTH;
    const height = CANVAS_HEIGHT;
    const esc = (v) =>
      Number.isFinite(v) ? Number(v).toFixed(2) : "0";

    const body = shapesList
      .map((s) => {
        const rot = s.rotation || 0;
        const { cx, cy } = getShapeCenter(s);
        const transform = rot
          ? ` transform="rotate(${esc(rot)} ${esc(cx)} ${esc(cy)})"`
          : "";

        const stroke = s.color || "#000";
        const sw = s.strokeWidth || 3;
        const fill =
          s.fillColor && s.fillColor !== "transparent"
            ? s.fillColor
            : "none";

        if (s.type === "pen") {
          const pts = s.points || [];
          if (!pts.length) return "";
          const d =
            "M " +
            pts
              .map((p, idx) =>
                idx === 0
                  ? `${esc(p.x)} ${esc(p.y)}`
                  : `L ${esc(p.x)} ${esc(p.y)}`
              )
              .join(" ");
          return `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${esc(
            sw
          )}"${transform}/>`;
        }

        if (s.type === "rect") {
          return `<rect x="${esc(s.x)}" y="${esc(
            s.y
          )}" width="${esc(s.w)}" height="${esc(
            s.h
          )}" fill="${fill}" stroke="${stroke}" stroke-width="${esc(
            sw
          )}"${transform}/>`;
        }

        if (s.type === "ellipse") {
          return `<ellipse cx="${esc(s.cx)}" cy="${esc(
            s.cy
          )}" rx="${esc(s.rx)}" ry="${esc(
            s.ry
          )}" fill="${fill}" stroke="${stroke}" stroke-width="${esc(
            sw
          )}"${transform}/>`;
        }

        if (s.type === "line") {
          return `<line x1="${esc(s.x1)}" y1="${esc(
            s.y1
          )}" x2="${esc(s.x2)}" y2="${esc(
            s.y2
          )}" fill="none" stroke="${stroke}" stroke-width="${esc(
            sw
          )}"${transform}/>`;
        }

        if (s.type === "triangle") {
          const pts = (s.points || []).map((p) => `${esc(p.x)},${esc(p.y)}`);
          if (!pts.length) return "";
          return `<polygon points="${pts.join(" ")}" fill="${fill}" stroke="${stroke}" stroke-width="${esc(
            sw
          )}"${transform}/>`;
        }

        return "";
      })
      .join("");

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${body}</svg>`;
  }

  const handleOk = () => {
    console.log('🎨 handleOk called - NEW VERSION WITH NO GRID!');

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create a temporary canvas WITHOUT the grid
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = CANVAS_WIDTH;
    tempCanvas.height = CANVAS_HEIGHT;
    const tempCtx = tempCanvas.getContext('2d');

    console.log('✅ Creating clean canvas without grid');

    // Fill with white background (no grid!)
    tempCtx.fillStyle = '#fff';
    tempCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);


    // Draw only the shapes (no grid, no selection)
    shapes.forEach((shape) => drawShape(tempCtx, shape));

    console.log('✅ Shapes drawn, grid skipped');

    // Export the clean canvas
    const pngDataUrl = tempCanvas.toDataURL('image/png');

    const svgString = buildSvg(shapes);
    // keep SVG somewhere accessible
    if (typeof window !== 'undefined') {
      window.BLOCKZIE_JR_LAST_PAINT_SVG = svgString;
    }

    console.log('✅ Saving clean image without grid');
    if (onSave) onSave(pngDataUrl, headingText);
  };

  // ----------------------------------------------------
  // render
  // ----------------------------------------------------
  return (
    <div className="sjrpaint-modal">
      <div className="sjrpaint-modal-top">
        <input
          type="text"
          className="sjrpaint-heading-input"
          value={headingText}
          onChange={handleHeadingChange}
          maxLength={40}
          placeholder="Name your creation"
        />
      </div>
      <div className="sjrpaint-close-btn" onClick={onClose}>
        <img
          src="./assets/ui/closeit.svg"
          alt="Close"
          title="Close without saving"
        />
      </div>
      {/* main content */}
      <div className="sjrpaint-main">

        {/* left tools */}
        <div className="sjrpaint-tools-col">
          <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/undoOff.svg"
            alt="Undo"
            title="Undo"
            style={{ marginBottom: 13 }}
            onClick={handleUndo}
          />
          <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/redoOff.svg"
            alt="Redo"
            title="Redo"
            style={{ marginBottom: 17 }}
            onClick={handleRedo}
          />

          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "pen" ? " active" : "")
            }
            src="./assets/paint/pathOff.svg"
            alt="Pen"
            title="Pen"
            onClick={() => setTool("pen")}
          />

          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "rect" ? " active" : "")
            }
            src="./assets/paint/rectOff.svg"
            alt="Rectangle"
            title="Rectangle"
            onClick={() => setTool("rect")}
          />

          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "ellipse" ? " active" : "")
            }
            src="./assets/paint/ellipseOff.svg"
            alt="Ellipse"
            title="Ellipse"
            onClick={() => setTool("ellipse")}
          />

          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "triangle" ? " active" : "")
            }
            src="./assets/paint/triOff.svg"
            alt="Triangle"
            title="Triangle"
            onClick={() => setTool("triangle")}
          />

          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "line" ? " active" : "")
            }
            src="./assets/paint/Linee.svg"
            alt="Line"
            title="Line"
            onClick={() => setTool("line")}
          />

          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "eraser" ? " active" : "")
            }
            src="./assets/paint/Eraser.svg"
            alt="Eraser"
            title="Eraser (remove shapes you touch)"
            onClick={() => setTool("eraser")}
          />
        </div>

        {/* canvas column */}
        <div className="sjrpaint-center-column">
          <div className="sjrpaint-canvas-panel">
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              className="sjrpaint-canvas"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />

          </div>

          {/* color palette */}
          <div className="sjrpaint-bottom-palette">
            {COLORS.map((c) => (
              <button
                key={c}
                className="sjrpaint-palette-color"
                style={{
                  background: c,
                  border:
                    c === color ? "3px solid #1663c7" : "2px solid #ddd"
                }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>

        {/* right actions */}
        <div className="sjrpaint-actions-col">
          {/* move/select */}
          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "move" ? " active" : "")
            }
            src="./assets/paint/selectOff.svg"
            alt="Move"
            title="Move / Select"
            onClick={handleMoveTool}
          />

          {/* flip placeholder (future) */}
          {/* <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/flip.png"
            alt="Flip"
            title="Flip (reserved)"
          /> */}

          {/* rotate +15° each tap (uses camera icon slot to avoid new asset) */}
          <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/rotateOff.svg"
            alt="Rotate"
            title="Rotate selected by 15°"
            onClick={handleRotate}
          />

          {/* stamp */}
          <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/stamperOff.svg"
            alt="Stamp"
            title="Duplicate selected"
            onClick={handleStamp}
          />

          {/* cut */}
          <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/scissorsOff.svg"
            alt="Cut"
            title="Delete selected"
            onClick={handleCut}
          />

          {/* fill tool */}
          <img
            className={
              "sjrpaint-toolbar-icon" +
              (tool === "fill" ? " active" : "")
            }
            src="./assets/paint/paintbucketOff.svg"
            alt="Fill"
            title="Fill shape with current color"
            onClick={handleFillTool}
            style={{ marginTop: 6 }}
          />

          {/* ok/save */}
          <img
            className="sjrpaint-toolbar-icon"
            src="./assets/paint/ok.svg"
            alt="OK"
            title="Save drawing"
            onClick={handleOk}
            style={{
              marginTop: 6,
              background: "#bce2b2",
              borderRadius: "12px"
            }}
          />

          <div className="sjrpaint-size-stack">
            {THICKNESS_LEVELS.map((level) => (
              <button
                key={level}
                className={
                  "sjrpaint-thickness-option" +
                  (strokeWidth === level ? " active" : "")
                }
                onClick={() => setStrokeWidth(level)}
              >
                <span
                  className="sjrpaint-thickness-dot"
                  style={{
                    width: level * 2,
                    height: level * 2
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
